import React, { useState } from 'react';
import { useToggle } from 'react-use';
import axios from 'axios';
import { Card } from '../../design-system';
import { FaEdit } from 'react-icons/fa';
import Alert from '../../helpers/alert';
import { successRefetch } from '../../helpers/data-loader';
import { Table, TableBody, TableCardHeader } from '../../components/Table';
import ModalForm from '../../components/ModalForm';
import { NOTES_FIELD, ERR_MSG } from '../../globalConstants';
import { newformatTime } from '../../helpers/utils';
import DescriptionTooltip from '../../components/DescriptionTooltip';
import ButtonCellColumn from '../../components/ButtonCellColumn';
import DeleteCellColumn from '../../components/DeleteCellColumn';

interface ProjectNotesProps {
  data: any[];
  projectId: any;
  refetch: () => void;
  canEdit: boolean;
  handleDeleteNote: (noteId: any) => void;
}

const ProjectNotes = ({
  data,
  projectId,
  refetch,
  canEdit,
  handleDeleteNote,
}: ProjectNotesProps) => {
  const [notesModal, toggleNotesModal] = useState(false);
  const [editNotesModal, toggleEditNotesModal] = useToggle(false);
  const [noteValues, toggleNoteValues] = useState<any>();

  const toggleEditModal = (id?: any) => {
    if (editNotesModal) {
      toggleEditNotesModal(false);
    } else {
      const note = data.find((m) => m.id === id);
      toggleNoteValues(note);
      toggleEditNotesModal(true);
    }
  };

  const handleNoteSubmit = async (values: any) => {
    const payload = {
      ...values,
      modelId: projectId,
      modelType: 'AppModelsProject',
    };
    try {
      await axios.post('/note', payload);
      toggleNotesModal(false);
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleUpdateNoteSubmit = async (values: any) => {
    try {
      await axios.put(`/note/${values.id}`, values);
      toggleEditNotesModal(false);
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const NOTES_COLUMNS = [
    {
      Header: 'Date',
      accessor: 'created_at',
      Cell: ({ cell: { value } }) => newformatTime(value),
    },
    {
      Header: 'By',
      accessor: 'creator.name',
    },
    {
      Header: 'Note',
      accessor: 'note',
      Cell: (props) => <DescriptionTooltip {...props} maxLength={255} />,
    },
    ...(canEdit
      ? [
          {
            Header: 'Edit',
            disableSortBy: true,
            Cell: ({
              row: {
                original: { id },
              },
            }) => (
              <ButtonCellColumn
                onClick={() => toggleEditModal(id)}
                leftIcon={FaEdit}
              />
            ),
          },
          {
            Header: 'Delete',
            accessor: 'handleDeleteNote',
            disableSortBy: true,
            Cell: DeleteCellColumn,
          },
        ]
      : []),
  ];

  return (
    <Card mt={10}>
      <TableCardHeader
        name="Notes"
        action="Add Note"
        onClick={() => toggleNotesModal(true)}
        disabled={!canEdit}
      />
      <TableBody {...({ maxHeight: '25rem' } as any)}>
        <Table
          name="Notes"
          columns={NOTES_COLUMNS}
          data={data.map((item) => ({
            ...item,
            toggleEditModal,
            handleDeleteNote,
            canEdit,
          }))}
          initialSortBy={[{ id: 'created_at', desc: true }]}
        />
      </TableBody>
      <ModalForm
        title="Add Note"
        isOpen={notesModal}
        onSubmit={handleNoteSubmit}
        onClose={() => toggleNotesModal(false)}
        fields={NOTES_FIELD}
        oneColumn
      />
      <ModalForm
        title="Edit Note"
        isOpen={editNotesModal}
        onClick={toggleEditModal}
        onSubmit={handleUpdateNoteSubmit}
        onClose={toggleEditModal}
        fields={NOTES_FIELD}
        oneColumn
        initialValues={noteValues}
      />
    </Card>
  );
};

export default ProjectNotes;
